import { Component, OnInit, inject, model, viewChild } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';

import { BaseComponent } from '../base/base.component';
import { FileTypes } from '../../constants';
import { AttachedDocuments, DragAndDropDocument, FileData, ReportTemplate } from '../../models';
import { DragAndDropService, FileService, ModalService, NotificationService } from '../../services';
import { FormlyService } from '../../services/component-services/formly.service';
import { GridService } from '../../services/component-services/grid.service';
import { SummaryTopComponent } from '../summary-top/summary-top.component';
import { BaseInlineFormComponent } from '../base-inline-form/base-inline-form.component';
import { DragAndDropComponent } from '../drag-and-drop/drag-and-drop.component';
import { GridComponent } from '../grids/grid/grid.component';

@Component({
  selector: 'app-email-modal',
  templateUrl: './email-modal.component.html',
  styleUrl: './email-modal.component.scss',
  imports: [SummaryTopComponent, BaseInlineFormComponent, GridComponent, DragAndDropComponent],
})
export class EmailModalComponent extends BaseComponent implements OnInit {
  private readonly dragAndDropService = inject(DragAndDropService);
  private readonly fileService = inject(FileService);
  private readonly formlyService = inject(FormlyService);
  private readonly gridService = inject(GridService);
  private readonly modalService = inject(ModalService);
  private readonly notificationService = inject(NotificationService);

  private readonly grid = viewChild<GridComponent>('contactsGrid');

  public readonly reportTemplate = model<ReportTemplate>({
    ccEmail: '',
    email: '',
    emailBody: '',
    emailContacts: [],
    emailSubject: '',
    report: '',
    sellingCompany: '',
    suffix: '',
    template: undefined,
  });

  protected fields: FormlyFieldConfig[];
  protected readonly header = $localize`Email`;
  protected readonly gridHeader = $localize`Email Contacts`;
  protected readonly submitText = $localize`Send Email`;
  protected readonly submitIcon = 'envelope';
  protected readonly gridCssClass = 'email-modal-grid';

  protected readonly additionalHeader = $localize`Additional Attachments`;
  protected readonly dragAndDropClass = 'dropdiv-small';
  protected readonly dragAndDropProgressClass = 'progress-small';
  protected readonly dragAndDropHeader = $localize`Add`;
  protected readonly dragAndDropText = $localize`Click HERE or drag file to attach to email`;
  protected readonly processingText = $localize`Attaching files`;

  protected attachedDocuments: AttachedDocuments[];

  private dragAndDropDocument: DragAndDropDocument = { folderName: '', subFolderName: '', fileName: '', container: '' };
  private fileFormData: FormData;

  protected readonly attachedColumnDefs: ColDef[] = [this.gridService.text('documentName', $localize`Document Name`)];

  protected columnDefs: ColDef[] = [
    ...this.gridService.checkboxRowSelect('forename', $localize`Forename`, true),
    this.gridService.text('surname', $localize`Surname`),
    this.gridService.text('email', $localize`Email`),
  ];

  ngOnInit() {
    this.dragAndDropDocument.folderName = this.reportTemplate().emailSubject;
    this.dragAndDropDocument.subFolderName = $localize`Attachments`;
    this.getFields();
    this.listFiles();
  }

  private getFields() {
    this.fields = [
      this.formlyService.text('email', $localize`To Email`),
      this.formlyService.text('ccEmail', $localize`CC Email`),
      this.formlyService.text('emailSubject', $localize`Subject`),
      this.formlyService.textarea('emailBody', $localize`Body`),
    ];
  }

  email() {
    this.reportTemplate().emailContacts = this.grid()?.selectedRows;
    this.modalService.close(this.reportTemplate);
  }

  attachFiles(file: FileData) {
    this.dragAndDropDocument.fileName = file.name;

    const fileType = file.name.substring(file.name.lastIndexOf('.') + 1);
    if (
      fileType === FileTypes.jpeg ||
      fileType === FileTypes.jpg ||
      fileType === FileTypes.png ||
      fileType === FileTypes.bmp
    ) {
      this.fileFormData = this.fileService.imageToFormData(file.src);
      this.dragAndDropService.uploadImage(this.dragAndDropDocument, this.fileFormData).subscribe();
    } else {
      this.fileFormData = this.fileService.fileToFormData(file.src);
      this.dragAndDropService.uploadFile(this.dragAndDropDocument, this.fileFormData).subscribe();
    }
    this.listFiles();
    this.notificationService.success($localize`File attached successfully`);
  }

  openDocument(row: any) {
    this.dragAndDropService.openPreview(row.fullPath);
  }

  removeDocument(model: any) {
    this.dragAndDropService.removeFile(model.fullPath).subscribe(() => {
      this.listFiles();
      this.notificationService.success($localize`File removed successfully`);
    });
  }

  private listFiles() {
    setTimeout(() => {
      this.dragAndDropService
        .listFiles(this.dragAndDropDocument)
        .subscribe((model: AttachedDocuments[]) => (this.attachedDocuments = model));
    }, 0);
  }
}
